import api, { objectToFormData } from './api';

export type LiveReviewQrKind = 'appointment' | 'walkin' | 'poster';

export interface LiveReviewQrResponse {
  qrToken: string;
  reviewUrl: string;
  kind: LiveReviewQrKind;
  walkinJti?: string;
}

interface PhotoUploadResponse {
  success: boolean;
}

// Generate a QR for a Quick Live Review.
// With a reservationId -> per-appointment (7-day, verified) QR.
// Without -> static reusable poster QR (NOT a verified visit).
export const generateReviewQr = async (reservationId?: string): Promise<LiveReviewQrResponse> => {
  try {
    const response = await api.post<LiveReviewQrResponse>('/business/live-review/qr', reservationId ? { reservationId } : {});
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error generating live review QR:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

// Generate a verified, single-use WALK-IN QR (no reservation, no customer PII).
// Creates a lightweight walk-in visit and returns its id so a photo can be attached.
// `stylistId` is optional — a business owner may pick a specific stylist;
// a stylist token is scoped to themselves and the override is ignored server-side.
export const generateWalkinQr = async (stylistId?: string): Promise<LiveReviewQrResponse> => {
  try {
    const body: { walkin: true; staffId?: string; stylistId?: string } = { walkin: true };
    if (stylistId) {
      body.staffId = stylistId;
      body.stylistId = stylistId;
    }
    const response = await api.post<LiveReviewQrResponse>('/business/live-review/qr', body);
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error generating walk-in review QR:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};

// Store a customer photo privately against an appointment (reservationId) or a
// walk-in (walkinJti). The FE masks the photo via MediaPipe BEFORE calling this,
// so we forward photoMasked=true — the backend rejects un-masked uploads.
//
// `originalPhoto` is the un-masked raw capture; send it whenever the mode is
// anything other than 'original' so the consumer can offer the "blur salon
// original" toggle. `photoMode` is metadata for analytics (the BE expects
// 'original' | 'auto-masked' | 'manual-blurred'; the FE's 'manual' maps to
// 'manual-blurred').
export const uploadCustomerPhoto = async (
  target: { reservationId: string; photoMode: 'original' | 'manual' } | { walkinJti: string; photoMode: 'original' | 'manual' },
  file: File,
  originalPhoto?: File,
): Promise<PhotoUploadResponse> => {
  try {
    const { photoMode, ...targetIds } = target;
    const beMode = photoMode === 'manual' ? 'manual-blurred' : 'original';
    const formData = objectToFormData({ ...targetIds, photoMode: beMode, photo: file, photoMasked: true });
    if (originalPhoto) formData.append('originalPhoto', originalPhoto, originalPhoto.name);
    const response = await api.post<PhotoUploadResponse>('/business/live-review/photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error: unknown) {
    const axiosErr = error as { response?: { data?: unknown }; message?: string };
    console.error('Error uploading customer photo:', axiosErr.response?.data || axiosErr.message);
    throw error;
  }
};
